/**
 * Client-side image resize before upload. Canvas-based — no native deps, no
 * extra round trip. Preserves EXIF orientation via `createImageBitmap`'s
 * `imageOrientation: 'from-image'` (browsers honour the EXIF rotation tag
 * automatically that way), and lets transparent PNGs round-trip through
 * WebP without losing the alpha channel.
 *
 * Why client-side over server-side `sharp`:
 *   - server CPU + native deps for a feature most uploads don't need
 *   - editorial team uploads infrequently (handful per day)
 *   - rejected uploads still validate server-side as a safety net
 *
 * Skipped (returns original file unchanged) when:
 *   - file is already smaller than `skipBelowBytes` (1 MB by default)
 *   - file isn't a raster image type the browser can decode
 *   - canvas APIs are unavailable (very old browsers — graceful degrade)
 *   - the resize attempt throws (e.g. CORS issues on cross-origin Blob)
 *
 * Output format: WebP at quality 0.82 — sweet spot for visual quality vs
 * file size, alpha-preserving, supported by every modern browser. Falls
 * back to the original mime type when WebP encoding fails.
 */

export interface ResizeOptions {
  /** Largest width-or-height (whichever is bigger) the output can be. */
  maxDimension?: number;
  /** Files below this size skip resize entirely. */
  skipBelowBytes?: number;
  /** WebP/JPEG quality 0..1. Default 0.82. */
  quality?: number;
}

const DEFAULT_MAX_DIMENSION = 2048;
const DEFAULT_SKIP_BELOW = 1024 * 1024; // 1 MB
const DEFAULT_QUALITY = 0.82;

/**
 * Resize-if-needed. Returns the original file when no resize is required
 * (already small, or non-image, or browser can't help) — never throws so
 * the upload pipeline downstream stays simple.
 */
export async function resizeImageIfNeeded(
  file: File,
  opts: ResizeOptions = {},
): Promise<File> {
  const maxDim = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const skipBelow = opts.skipBelowBytes ?? DEFAULT_SKIP_BELOW;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  if (!file.type.startsWith('image/')) return file;
  if (file.size <= skipBelow) return file;
  if (typeof createImageBitmap !== 'function') return file;

  let bitmap: ImageBitmap;
  try {
    // `imageOrientation: 'from-image'` makes the bitmap arrive with EXIF
    // rotation already applied. Without it, an iPhone landscape photo
    // would render sideways after the canvas pass.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }

  const { width: srcW, height: srcH } = bitmap;
  // Only downscale — never upscale (would just inflate the file).
  if (srcW <= maxDim && srcH <= maxDim) {
    bitmap.close?.();
    return file;
  }

  const scale = Math.min(maxDim / srcW, maxDim / srcH);
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  let canvas: HTMLCanvasElement | OffscreenCanvas;
  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  try {
    if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(dstW, dstH);
      ctx = canvas.getContext('2d');
    } else {
      canvas = document.createElement('canvas');
      canvas.width = dstW;
      canvas.height = dstH;
      ctx = canvas.getContext('2d');
    }
  } catch {
    bitmap.close?.();
    return file;
  }
  if (!ctx) {
    bitmap.close?.();
    return file;
  }

  // Higher-quality bilinear/bicubic sampling. Browser-defined exactly which
  // algorithm — Chrome and Safari both produce visibly cleaner edges than
  // the nearest-neighbour default at large downscale ratios.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, dstW, dstH);
  bitmap.close?.();

  // Prefer WebP — smaller files at equivalent quality, alpha-preserving,
  // universally decoded by client renderers (browsers + every modern image
  // viewer). PNGs with transparency keep their alpha channel.
  const targetType = 'image/webp';
  const blob = await canvasToBlob(canvas, targetType, quality);
  if (!blob) return file;

  // Sanity check — a "resized" file that's actually bigger than the
  // original means the encoder couldn't beat the source compression.
  // Skip the resize in that case.
  if (blob.size >= file.size) return file;

  const ext = targetType === 'image/webp' ? 'webp' : extOf(file.name) ?? 'jpg';
  const baseName = stripExt(file.name) || 'image';
  return new File([blob], `${baseName}.${ext}`, { type: targetType });
}

function canvasToBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  quality: number,
): Promise<Blob | null> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type, quality }).catch(() => null);
  }
  return new Promise((resolve) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) => resolve(b),
      type,
      quality,
    );
  });
}

function extOf(name: string): string | null {
  const i = name.lastIndexOf('.');
  if (i < 0 || i === name.length - 1) return null;
  return name.slice(i + 1).toLowerCase();
}

function stripExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? name : name.slice(0, i);
}
